import { concat } from '@ember/helper';
import { action, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismTable,
  PrismTableCellsTdTitleAttribute,
  styleEntry,
} from '@mylighthouse/prism-ember';
import HotelStrategyTablesCellsTdHotelNested from 'lumen/components/hotel/strategy/tables/cells/td-hotel-nested';
import HotelStrategyTablesCellsTdStrategyBaserate from 'lumen/components/hotel/strategy/tables/cells/td-strategy-baserate';
import HotelStrategyTablesCellsThStrategyDetail from 'lumen/components/hotel/strategy/tables/cells/th-strategy-detail';
import {
  COLUMN_CONFIG,
  COLUMN_PROPERTIES,
  COLUMN_WIDTH,
  SUBTYPE_DELIMITER,
  SUBTYPE_VALUES_KEY_PATTERN,
} from 'lumen/utils/constants/rate-strategy-constants';
import {
  sortHelper,
  subSortRowsFromSameHotel,
} from 'lumen/utils/strategy/detail-view-helpers';

interface HotelStrategyTablesTableDetailRoomtypeSignature {
  Args: {
    containerHeight: unknown;
    onTableSort: unknown;
    sortBy: unknown;
    sortDirection: unknown;
    sorts: unknown;
    subtypes: unknown;
  };
}

export default class HotelStrategyTablesTableDetailRoomtype extends Component<HotelStrategyTablesTableDetailRoomtypeSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  @tracked expandedRowIds = new Set();

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const valuePathHotel = COLUMN_PROPERTIES.HOTELINFO;
    const columns = [
      {
        ...COLUMN_CONFIG.HOTELINFO_HEADER,
        subcolumns: [
          {
            ...COLUMN_CONFIG.HOTELINFO,
            // eslint-disable-next-line @typescript-eslint/unbound-method
            clickNestedRow: this.clickNestedRow,
            name: this.intl.t('general.hotels'),
            tdComponent: HotelStrategyTablesCellsTdHotelNested,
            valuePath: valuePathHotel,
          },
        ],
      },
    ];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.subtypes.forEach((subtype) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const valuePathRate = `${subtype.id}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.RATE}`;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const displayValuePathRate = `${subtype.id}.${COLUMN_PROPERTIES.RATE}`;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const valuePathRoomName = `${subtype.id}${SUBTYPE_DELIMITER}${COLUMN_PROPERTIES.ROOM_NAME}`;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const displayValuePathRoomName = `${subtype.id}.${COLUMN_PROPERTIES.ROOM_NAME}.formattedValue`;

      columns.push({
        thComponent: HotelStrategyTablesCellsThStrategyDetail,
        width: COLUMN_WIDTH.DEFAULT + COLUMN_WIDTH.LARGE,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        columnKey: subtype.label,
        textAlign: 'center',
        hasRightBorder: true,
        hasCompareButton: true,
        subtypeHeaderAttributes: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          isMobileBookingSubtype: subtype.isMobileBookingSubtype,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          isMobileExpediaSubtype: subtype.isMobileExpediaSubtype,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          isMealtypeType: subtype.isMealtypeType,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          isPosBookingSubtype: subtype.isPosBookingSubtype,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          isPosExpediaSubtype: subtype.isPosExpediaSubtype,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          isBranddotcomMemberRatesSubType:
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            subtype.isBranddotcomMemberRatesSubType,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          isMemberRatesType: subtype.isMemberRatesType,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          label: subtype.label,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          formattedLabel: subtype.formattedLabel[SUBTYPE_VALUES_KEY_PATTERN],
        },
        subcolumns: [
          {
            name: this.intl.t('rates.rate'),
            valuePath: valuePathRate,
            displayValuePath: displayValuePathRate,
            // @ts-expect-error: Incorrect type
            tdComponent: HotelStrategyTablesCellsTdStrategyBaserate,
            textAlign: 'center',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            columnKey: subtype.label,
            width: COLUMN_WIDTH.DEFAULT,
          },
          {
            name: this.intl.t('general.room_name'),
            valuePath: valuePathRoomName,
            displayValuePath: displayValuePathRoomName,
            // @ts-expect-error: Incorrect type
            tdComponent: PrismTableCellsTdTitleAttribute,
            tdClassNames: 'text-200--regular truncate',
            textAlign: 'center',
            hasRightBorder: true,
            width: COLUMN_WIDTH.LARGE,
          },
        ],
      });
    });

    return columns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parentRowReferenceMap() {
    return new Map(
      this.rows
        .filter((row) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          return !row[COLUMN_PROPERTIES.HOTELINFO].isChildRow;
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .map((row) => [row.hotelinfo.id, row]),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    // @ts-expect-error: Incorrect type
    const rows = [];

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const roomtypeData = [...this.args.roomtypeDataPerHotelInfo.values()];

    roomtypeData.forEach((headerRowData) => {
      // No expand chevron and action for header rows without subrows
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (!headerRowData.numOfSubRows) {
        // Ember set for property tracked by a tracking context
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        set(headerRowData[COLUMN_PROPERTIES.HOTELINFO], 'isExpandable', false);
      }

      const isRowExpanded = this.expandedRowIds.has(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        headerRowData[COLUMN_PROPERTIES.HOTELINFO].id,
      );

      // Ember set for property tracked by a tracking context
      set(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        headerRowData[COLUMN_PROPERTIES.HOTELINFO],
        'isExpanded',
        isRowExpanded,
      );

      rows.push(headerRowData);

      if (isRowExpanded) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        for (let i = 0; i < headerRowData.numOfSubRows; i++) {
          const subRow = {
            // Used in the subrow sort logic and to hide the hotel name for the subrows
            [COLUMN_PROPERTIES.HOTELINFO]: {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              id: headerRowData.hotelinfo.id,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              formattedValue: headerRowData.hotelinfo.formattedValue,
              isOwnHotelInfo: this.hotelAndMarketService.isOwnHotelInfo(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                headerRowData.hotelinfo,
              ),
              isChildRow: true,
            },
          };

          // Check for each room type if we can fill a cell at the current row index.
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          this.args.subtypes.forEach((subtype) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (i < headerRowData[subtype.id].subpatterns.length) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              subRow[subtype.id] = headerRowData[subtype.id].subpatterns[i];
            }
          });

          rows.push(subRow);
        }
      }
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return rows;
  }

  @action
  // @ts-expect-error: Incorrect type
  clickNestedRow(hotelinfoId): void {
    if (this.expandedRowIds.has(hotelinfoId)) {
      this.expandedRowIds.delete(hotelinfoId);
    } else {
      this.expandedRowIds.add(hotelinfoId);
    }
    // Ember set is necessary to trigger updating the @tracked property
    set(this, 'expandedRowIds', this.expandedRowIds);
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sortFunction(rowA, rowB) {
    /**
     * Sort function for ember-table
     * @param {Object} rowA - First row to compare
     * @param {Object} rowB - Second row to compare
     * @returns {Number} -1, 0, or 1
     */
    const { sortDirection, sortBy } = this.args;
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const splitSortKey = sortBy.split(SUBTYPE_DELIMITER);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const subtype = splitSortKey[0]; // e.g. best_flex
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const column = splitSortKey[1]; // e.g. rate

    if (
      sortBy !== COLUMN_PROPERTIES.HOTELINFO &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (rowA[sortBy] ?? rowA[subtype])
    ) {
      // Map the header rows hotelinfo id on the row objects: these will be used as reference value for the subrows in the sort
      const { parentRowReferenceMap } = this;

      if (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        rowA[COLUMN_PROPERTIES.HOTELINFO].id ===
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        rowB[COLUMN_PROPERTIES.HOTELINFO].id
      ) {
        const sortOrder = subSortRowsFromSameHotel(rowA, rowB);
        // If an sortOrder could be determined (sortOrder = -1 or 1), return it. If not (sortOrder = 0), use the sort helper to determine the sort order
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return (
          sortOrder || sortHelper(rowA, rowB, subtype, column, sortDirection)
        );
      }

      // For sorting between rows from different hotels always use the header rows as reference!
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return sortHelper(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        parentRowReferenceMap.get(rowA[COLUMN_PROPERTIES.HOTELINFO].id),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        parentRowReferenceMap.get(rowB[COLUMN_PROPERTIES.HOTELINFO].id),
        subtype,
        column,
        sortDirection,
      );
    }
    return 0;
  }

  <template>
    <PrismTable
      {{! @glint-expect-error: Incorrect type }}
      @columns={{this.columns}}
      @enableAutoSortsUpdate={{true}}
      @fillMode="first-column"
      {{! @glint-expect-error: Incorrect type }}
      @initialSorts={{@sorts}}
      {{! @glint-expect-error: Incorrect type }}
      @onTableSort={{@onTableSort}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      @sortFunction={{this.sortFunction}}
      @widthConstraint="eq-container-slack"
      class="rate-strategy-table"
      style={{styleEntry "height" (concat @containerHeight "px")}}
    />
  </template>
}
